import { Box, Typography } from '@mui/material'
import React from 'react'
import { ActionLog, Control } from '../../types'
import { categoryStr } from '../../utils'
import { toHms } from '../../utils/businessUtil'
import { CategoryIcon } from '../MachineTable/MachineIcon'

export type Props = {
	control: Control
	actionLog: ActionLog
	detail?: boolean
}

// タグ(トリガー種別)で左帯の色を決める。
const TAG_STRIPE: Record<string, string> = {
	btn: '#e0d868', // ボタン
	rainclose: '#73e3ff', // 雨で閉じる
	'': '#885588', // 自動実行
}

type Status = { label: string; fg: string; bg: string }

// 状態ピル。優先度: キャンセル > 実行/待機 > 補正 > トリガー種別。
const statusOf = (a: ActionLog): Status => {
	if (a.cancelled)
		return { label: 'キャンセル', fg: '#c1272d', bg: 'rgba(214,69,69,.14)' }
	if (a.progress === 1)
		return { label: '実行中', fg: '#00817a', bg: 'rgba(0,163,154,.14)' }
	if (a.progress === 2)
		return { label: '待機中', fg: '#b26a2e', bg: 'rgba(232,131,58,.16)' }
	if (a.progress === 3)
		return {
			label: 'クールタイム待機中',
			fg: '#b26a2e',
			bg: 'rgba(232,131,58,.16)',
		}
	if (a.corrected)
		return { label: '補正終了', fg: '#5a4bbf', bg: 'rgba(106,90,205,.14)' }
	if (a.tag === 'btn')
		return { label: 'ボタン', fg: '#8a7d1f', bg: 'rgba(224,216,104,.24)' }
	if (a.tag === 'rainclose')
		return { label: '雨で閉じる', fg: '#2f7d9c', bg: 'rgba(115,227,255,.24)' }
	return { label: '自動', fg: '#6b5a6b', bg: 'rgba(136,85,136,.14)' }
}

export default function LogCard({ actionLog, control, detail = false }: Props) {
	const cateLabel = categoryStr[control.category]
	const name = control.label.includes(cateLabel)
		? control.label
		: `${cateLabel} ${control.label}`
	const status = statusOf(actionLog)
	const stripe = TAG_STRIPE[actionLog.tag] ?? TAG_STRIPE['']

	return (
		<Box
			className="action-log-card"
			data-tag={actionLog.tag}
			data-cancelled={actionLog.cancelled}
			data-corrected={actionLog.corrected}
			data-progress={actionLog.progress}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				py: 0.75,
				px: 1,
				my: 0.5,
				borderRadius: 1,
				border: '1px solid',
				borderColor: 'divider',
				borderLeft: `4px solid ${stripe}`,
				opacity: actionLog.cancelled ? 0.55 : 1,
			}}
		>
			<CategoryIcon
				category={control.category}
				stepRate={6}
				style={{ width: '24px', height: '24px', flexShrink: 0 }}
			/>

			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					noWrap
					sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.3 }}
				>
					{name}
					<Box
						component="span"
						sx={{ color: 'primary.main', fontWeight: 700, ml: 0.5 }}
					>
						→{actionLog.endRate ?? 0}%
					</Box>
				</Typography>
				<Typography
					sx={{
						color: 'text.secondary',
						fontSize: 11,
						fontVariantNumeric: 'tabular-nums',
					}}
				>
					{detail && actionLog.realStartAt && (
						<>
							({toHms(actionLog.realStartAt)}〜{toHms(actionLog.realEndAt)}){' '}
						</>
					)}
					検知 {toHms(actionLog.createdAt)}
				</Typography>
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-end',
					gap: 0.5,
					flexShrink: 0,
				}}
			>
				<Typography
					sx={{
						color: 'text.secondary',
						fontSize: 11,
						fontVariantNumeric: 'tabular-nums',
					}}
				>
					{toHms(actionLog.startAt)}
					{actionLog.endAt && <>〜{toHms(actionLog.endAt)}</>}
				</Typography>
				<Box
					sx={{
						fontSize: 10.5,
						fontWeight: 700,
						px: 1,
						py: '2px',
						borderRadius: 999,
						color: status.fg,
						bgcolor: status.bg,
						whiteSpace: 'nowrap',
					}}
				>
					{status.label}
				</Box>
			</Box>
		</Box>
	)
}
